import React from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { ROUTE_EXPORT_FORMATS } from "@/config/routeExportFormats";
import CustomRadio from "@/components/ui/CustomRadio.jsx";
import WarnChip from "@/components/ui/WarnChip.jsx";

const FIELD_SX = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 1,
    backgroundColor: "rgba(0,79,125,0.04)",
  },
};

const SECTION_LABEL_SX = {
  fontSize: 11,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.4px",
  color: "#8a98a6",
  mb: 0.75,
};

function OptionLabel({ title, subtitle }) {
  return (
    <Box sx={{ py: 0.25 }}>
      <Typography sx={{ fontSize: 14, lineHeight: 1.3 }}>{title}</Typography>
      {subtitle ? (
        <Typography sx={{ fontSize: 12, color: "text.secondary", lineHeight: 1.3 }}>
          {subtitle}
        </Typography>
      ) : null}
    </Box>
  );
}

export default function RouteActionDialog({
  open,
  title,
  description,
  routeName,
  onRouteNameChange,
  voyageMode = "none",
  vesselName = "",
  activeVoyageName = "",
  saveTarget = "add",
  onSaveTargetChange,
  voyageName = "",
  onVoyageNameChange,
  showFormat = false,
  format = "geojson",
  formatOptions = ROUTE_EXPORT_FORMATS,
  onFormatChange,
  error = "",
  saving = false,
  confirmLabel = "Save",
  onCancel,
  onConfirm,
  disableConfirm = false,
}) {
  return (
    <Dialog
      open={open}
      onClose={() => {
        if (saving) return;
        onCancel?.();
      }}
      disableRestoreFocus
      maxWidth="xs"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: 2,
            overflow: "hidden",
            boxShadow: 24,
          },
        },
      }}
    >
      <DialogTitle sx={{ fontSize: 18, fontWeight: 700, color: "#004f7d", pb: 0.5 }}>
        {title}
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Box
          sx={{
            "& .MuiInputBase-input": {
              fontSize: 14,
            },
            "& .MuiFormLabel-root": {
              fontSize: 14,
            },
          }}
        >
          <DialogContentText sx={{ fontSize: 13, mb: 1.25, color: "text.secondary" }}>
            {description}
          </DialogContentText>
          {error ? (
            <Alert severity="error" sx={{ mb: 1.25 }}>
              {error}
            </Alert>
          ) : null}
          {voyageMode === "choose" ? (
            <Box sx={{ mb: 1.5 }}>
              <Typography sx={SECTION_LABEL_SX}>Voyage</Typography>
              <RadioGroup
                value={saveTarget}
                onChange={(e) => onSaveTargetChange?.(e.target.value)}
              >
                <FormControlLabel
                  value="add"
                  disabled={!activeVoyageName}
                  control={<CustomRadio size="small" />}
                  label={
                    <OptionLabel
                      title={activeVoyageName ? `Add to '${activeVoyageName}'` : "Add to active voyage"}
                      subtitle={
                        activeVoyageName
                          ? "Saved as suggested route"
                          : `${vesselName || "This vessel"} has no active voyage`
                      }
                    />
                  }
                />
                <FormControlLabel
                  value="new"
                  control={<CustomRadio size="small" />}
                  label={<OptionLabel title="Start new voyage" />}
                />
              </RadioGroup>
              {saveTarget === "new" ? (
                <>
                  <TextField
                    autoFocus
                    fullWidth
                    size="small"
                    label="Voyage name"
                    error={!voyageName.trim()}
                    helperText={voyageName.trim() ? "" : "Enter a voyage name."}
                    value={voyageName}
                    onChange={(e) => onVoyageNameChange?.(e.target.value)}
                    sx={{ ...FIELD_SX, mt: 1, "& .MuiFormHelperText-root": { fontSize: 11, ml: 0.25 } }}
                  />
                  {activeVoyageName ? (
                    <WarnChip severity="error" sx={{ mt: 1 }}>
                      &apos;{activeVoyageName}&apos; and its routes will be archived.
                    </WarnChip>
                  ) : null}
                </>
              ) : null}
            </Box>
          ) : null}
          <Typography sx={SECTION_LABEL_SX}>Route</Typography>
          <TextField
            autoFocus={!(voyageMode === "choose" && saveTarget === "new")}
            fullWidth
            size="small"
            label="Route name"
            value={routeName}
            onChange={(e) => onRouteNameChange?.(e.target.value)}
            sx={{ ...FIELD_SX, mb: showFormat ? 1.25 : 0 }}
          />
          {showFormat ? (
            <TextField
              select
              fullWidth
              size="small"
              label="Format"
              value={format}
              onChange={(e) => onFormatChange?.(e.target.value)}
              sx={FIELD_SX}
            >
              {formatOptions.map(({ value, label }) => (
                <MenuItem key={value} value={value} sx={{ fontSize: 14 }}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
          ) : null}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.25, pt: 0.75 }}>
        <Button
          variant="outlined"
          onClick={onCancel}
          disabled={saving}
          sx={{
            textTransform: "none",
            minWidth: 86,
            borderColor: "rgba(0,79,125,0.35)",
            color: "#004f7d",
            "&:hover": {
              borderColor: "#004f7d",
              backgroundColor: "rgba(0,79,125,0.06)",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={saving || disableConfirm}
          onClick={onConfirm}
          sx={{
            textTransform: "none",
            minWidth: 110,
            backgroundColor: "#004f7d",
            "&:hover": {
              backgroundColor: "#03608f",
            },
          }}
        >
          {saving ? (
            <CircularProgress size={18} sx={{ color: "#fff" }} />
          ) : (
            confirmLabel
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
